import { FormEvent, useState } from "react";
import { useLocation } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
	Box,
	Button,
	FormControl,
	FormLabel,
	HStack,
	Input,
	Stack,
	Text,
	Textarea,
} from "@chakra-ui/react";

import { Config } from "../../config/Config";
import { IContactRequest } from "../../models/IContactRequest";

export const FloatingContactWidget = (): JSX.Element | null => {
	const { pathname } = useLocation();
	const { executeRecaptcha } = useGoogleReCaptcha();

	const [open, setOpen] = useState(false);
	const [email, setEmail] = useState("");
	const [message, setMessage] = useState("");
	const [isSending, setIsSending] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const timestampMs = Date.now();

	// The contact-us page already hosts the full contact form, so skip the chip there.
	if (pathname === "/contact-us") {
		return null;
	}

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		setErrorMessage("");

		if (!executeRecaptcha) {
			setErrorMessage("reCAPTCHA is not ready, please try again in a few seconds.");
			return;
		}

		setIsSending(true);
		try {
			const token = await executeRecaptcha("contact_us");
			const payload: IContactRequest = {
				email,
				phoneNumber: "",
				message,
				website: "",
				recaptchaToken: token,
				timestamp: timestampMs,
			};
			const response = await fetch(`${Config.baseUrl}api/v1/contactrequest`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			});

			if (!response.ok) {
				throw new Error(`Contact request failed with status ${response.status}`);
			}

			setIsSubmitted(true);
		} catch (error) {
			console.error("Contact form submission error:", error);
			setErrorMessage("Something went wrong. Please try again later.");
		} finally {
			setIsSending(false);
		}
	};

	return (
		<Box
			position="fixed"
			right={{ base: "4", md: "6" }}
			bottom={{ base: "4", md: "6" }}
			zIndex={30}
		>
			{open ? (
				<Stack
					as="form"
					onSubmit={handleSubmit}
					bg="white"
					borderRadius="xl"
					boxShadow="2xl"
					border="1px solid"
					borderColor="gray.200"
					w={{ base: "calc(100vw - 2rem)", sm: "360px" }}
					maxW="360px"
					p="4"
					spacing="3"
					role="dialog"
					aria-label="Contact form"
				>
					<HStack justify="space-between" align="center">
						<Text fontWeight="bold" color="gray.800">Contact Us</Text>
						<Button size="xs" variant="ghost" onClick={() => setOpen(false)}>Close</Button>
					</HStack>

					{isSubmitted ? (
						<Text fontSize="sm" color="gray.600">
							Thanks for reaching out — we'll be in touch shortly.
						</Text>
					) : (
						<>
							<Text fontSize="sm" color="gray.600">
								Drop us a line for more information.
							</Text>
							<FormControl isRequired>
								<FormLabel fontSize="sm" mb="1">Email address</FormLabel>
								<Input
									type="email"
									placeholder="you@farm.com"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
								/>
							</FormControl>
							<FormControl>
								<FormLabel fontSize="sm" mb="1">Message (optional)</FormLabel>
								<Textarea
									placeholder="Tell us what you want to improve this season."
									rows={4}
									value={message}
									onChange={(e) => setMessage(e.target.value)}
								/>
							</FormControl>
							<Button
								type="submit"
								bg="green.600"
								color="white"
								_hover={{ bg: "green.700" }}
								isDisabled={!email.trim()}
								isLoading={isSending}
								loadingText="Sending"
							>
								Send
							</Button>
							{errorMessage && (
								<Text fontSize="sm" color="red.500">
									{errorMessage}
								</Text>
							)}
						</>
					)}
				</Stack>
			) : (
				<Button
					onClick={() => setOpen(true)}
					bg="green.600"
					color="white"
					_hover={{ bg: "green.700" }}
					boxShadow="lg"
					borderRadius="full"
					px="6"
					aria-label="Open contact form"
				>
					Contact Us
				</Button>
			)}
		</Box>
	);
};
